import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  CircularProgress, 
  Divider, 
  IconButton, 
  Menu, 
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Tooltip
} from '@mui/material';
import { useNavigate, useLocation, useNavigation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { getCognitoGroups } from '../auth/authUtils';
import { CaseListEntry } from '../Routes/CompanyAssessment/types';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { useCases } from '../contexts/CasesContext';
import api from '../lib/api';
import { useToast } from './Toast';
import ConfirmDialog from './ConfirmDialog';
import { useTranslation } from 'react-i18next';

interface CasesSidebarProps {
  sidebarCollapsed?: boolean;
}

const CasesSidebar: React.FC<CasesSidebarProps> = ({ sidebarCollapsed = false }) => {
  const { t } = useTranslation();
  const [cases, setCases] = useState<CaseListEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingCaseId, setLoadingCaseId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCaseForMenu, setSelectedCaseForMenu] = useState<CaseListEntry | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  
  // Dialog states
  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [newCaseName, setNewCaseName] = useState<string>('');
  const [operationLoading, setOperationLoading] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const auth = useAuth();
  const { refreshTrigger } = useCases();
  const toast = useToast();

  // Check if the user has access to the cases feature
  const casesEnabled = () => {
    const token = auth?.user?.access_token;
    if (token) {
      const groups = getCognitoGroups(token);
      return groups.includes('Feature_Cases');
    }
    return false;
  };

  // Get the current case ID from URL if we're on a company assessment page with a case
  const getCurrentCaseId = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('caseId');
  };

  useEffect(() => {
    const fetchCases = async () => {
      // Check if the user has access to the cases feature
      const token = auth?.user?.access_token;
      if (!token) {
        return;
      }
      
      const groups = getCognitoGroups(token);
      if (!groups.includes('Feature_Cases')) {
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const casesData = await api.get<CaseListEntry[]>('/v1/cases');
        setCases(casesData);
      } catch (err) {
        console.error('Error fetching cases:', err);
        setError(err instanceof Error ? err.message : t('assessment.details.fetchCasesFailed'));
        setCases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [auth?.user?.access_token, refreshTrigger, t]); // Re-fetch when auth changes or refresh is triggered

  const handleCaseClick = (caseEntry: CaseListEntry) => {
    // Set loading state for this specific case
    setLoadingCaseId(caseEntry.id);
    
    // Navigate to company assessment route with case as a URL parameter
    navigate(`/companyassessment/${caseEntry.orgNumber}?caseId=${caseEntry.id}`);
  };

  // Clear loading state when navigation completes
  useEffect(() => {
    if (navigation.state === 'idle') {
      setLoadingCaseId(null);
    }
  }, [navigation.state]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, caseEntry: CaseListEntry) => {
    event.stopPropagation(); // Prevent triggering the case click
    setMenuAnchorEl(event.currentTarget);
    setSelectedCaseForMenu(caseEntry);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // Don't clear selectedCaseForMenu here as it's needed for dialog operations
  };

  const handleRename = () => {
    if (selectedCaseForMenu) {
      setNewCaseName(selectedCaseForMenu.name);
      setRenameDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    if (!selectedCaseForMenu || !newCaseName.trim()) {
      return;
    }

    setOperationLoading(true);
    
    try {
      await api.patch(`/v1/cases/${selectedCaseForMenu.id}`, {
        name: newCaseName.trim(),
      });
      
      // Update the case in the local state
      setCases(prevCases => 
        prevCases.map(caseEntry => 
          caseEntry.id === selectedCaseForMenu.id 
            ? { ...caseEntry, name: newCaseName.trim() }
            : caseEntry
        )
      );

      setRenameDialogOpen(false);
      setNewCaseName('');
      setSelectedCaseForMenu(null);
      toast.success(t('assessment.details.caseRenamed'));
    } catch (err) {
      console.error('Error renaming case:', err);
      toast.error(t('assessment.details.caseRenameFailed', { message: err instanceof Error ? err.message : t('common.states.unknown') }));
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCaseForMenu) {
      return;
    }

    setOperationLoading(true);
    
    try {
      await api.delete(`/v1/cases/${selectedCaseForMenu.id}`);

      // Remove the case from the local state
      setCases(prevCases => 
        prevCases.filter(caseEntry => caseEntry.id !== selectedCaseForMenu.id)
      );

      // If the deleted case was currently selected, navigate away
      const currentCaseId = getCurrentCaseId();
      if (currentCaseId === selectedCaseForMenu.id) {
        navigate('/companyassessment');
      }

      setDeleteDialogOpen(false);
      setSelectedCaseForMenu(null);
      toast.success(t('assessment.details.caseDeleted'));
    } catch (err) {
      console.error('Error deleting case:', err);
      toast.error(t('assessment.details.caseDeleteFailed', { message: err instanceof Error ? err.message : t('common.states.unknown') }));
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!operationLoading) {
      setRenameDialogOpen(false);
      setDeleteDialogOpen(false);
      setNewCaseName('');
      setSelectedCaseForMenu(null);
    }
  };

  // Don't render anything if cases are not enabled for this user
  if (!casesEnabled()) {
    return null;
  }

  const currentCaseId = getCurrentCaseId();

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '240px'
    }}>
      {/* Subtle divider */}
      <Divider sx={{ my: 2, borderColor: 'divider' }} />
      
      {/* Cases Header */}
      {!sidebarCollapsed && (
        <Box 
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 1,
            pr: 1,
            mb: 0.5
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              px: 1, 
              py: 0.5, 
              color: 'text.secondary',
              fontSize: 'var(--font-size-small)',
              fontWeight: 'var(--font-weight-medium)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {t('assessment.cases.title')}
          </Typography>
          {isExpanded ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
        </Box>
      )}

      <Collapse in={isExpanded || sidebarCollapsed}>
        {/* Cases Content - Simple max height approach */}
        <Box sx={{
          maxHeight: '40vh', // Simple viewport-based height
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '4px',
          marginRight: '-4px', // Compensate for padding
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}>
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

        {/* Error State */}
        {error && !loading && (
          <Typography 
            variant="body2" 
            color="error" 
            sx={{ px: 1, py: 1, fontSize: 'var(--font-size-small)' }}
          >
            {t('assessment.details.fetchCasesFailed')}
          </Typography>
        )}

        {/* Cases List */}
        {!loading && !error && cases.length > 0 && (
          <List dense sx={{ py: 0 }}>
            {cases.map((caseEntry) => {
              const isSelected = currentCaseId === caseEntry.id;
              const isLoadingThisCase = loadingCaseId === caseEntry.id;
              
              return (
                <ListItem 
                  key={caseEntry.id} 
                  disablePadding
                  secondaryAction={
                    !sidebarCollapsed && (
                      <IconButton
                        edge="end"
                        aria-label={t('assessment.details.moreOptions')}
                        size="small"
                        onClick={(e) => handleMenuOpen(e, caseEntry)}
                        sx={{
                          color: isSelected ? 'primary.contrastText' : 'text.secondary',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.1)' : 'action.hover',
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <Tooltip 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {caseEntry.name}
                          </Typography>
                          <Typography variant="caption">
                            {t('common.fields.organizationNumber')}: {caseEntry.orgNumber}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <ListItemButton
                        onClick={() => handleCaseClick(caseEntry)}
                        selected={isSelected}
                        disabled={isLoadingThisCase}
                        sx={{
                          py: 0.5,
                          px: 1,
                          pr: sidebarCollapsed ? 1 : 5, // Add padding to prevent overlap with menu button
                          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                          borderRadius: 'var(--border-radius-sm)',
                          margin: '2px 0',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'primary.contrastText',
                            }
                          }
                        }}
                      >
                        {isLoadingThisCase ? (
                          <CircularProgress size={18} sx={{ mr: sidebarCollapsed ? 0 : 1 }} />
                        ) : (
                          <FolderIcon sx={{ mr: sidebarCollapsed ? 0 : 1, fontSize: 18 }} />
                        )}
                        {!sidebarCollapsed && (
                          <ListItemText
                            primary={caseEntry.name}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontSize: 'var(--font-size-small)',
                              fontWeight: isSelected ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)',
                              noWrap: true
                            }}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                </ListItem>
              );
            })}
          </List>
        )}

        {/* No Cases State */}
        {!loading && !error && cases.length === 0 && (
          <Typography 
            variant="body2" 
            sx={{ 
              px: 1, 
              py: 1, 
              color: 'text.secondary',
              fontSize: 'var(--font-size-small)',
              fontStyle: 'italic'
            }}
          >
            {t('assessment.cases.empty')}
          </Typography>
        )}
      </Box>
      </Collapse>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: 'var(--box-shadow-md)',
          }
        }}
      >
        <MenuItem onClick={handleRename} sx={{ fontSize: 'var(--font-size-small)' }}>
          {t('assessment.cases.rename')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ fontSize: 'var(--font-size-small)', color: 'error.main' }}>
          {t('common.actions.delete')}
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog 
        open={renameDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('assessment.cases.rename')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('assessment.details.renameCasePrompt', { name: selectedCaseForMenu?.name })}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('assessment.cases.name')}
            type="text"
            fullWidth
            variant="outlined"
            value={newCaseName}
            onChange={(e) => setNewCaseName(e.target.value)}
            disabled={operationLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCaseName.trim()) {
                handleRenameConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDialogClose} 
            disabled={operationLoading}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button 
            onClick={handleRenameConfirm} 
            variant="contained"
            disabled={operationLoading || !newCaseName.trim()}
            startIcon={operationLoading ? <CircularProgress size={20} /> : undefined}
          >
            {operationLoading ? t('assessment.details.renaming') : t('assessment.cases.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('assessment.cases.delete')}
        description={t('assessment.details.deleteCasePrompt', { name: selectedCaseForMenu?.name })}
        confirmLabel={operationLoading ? t('assessment.details.deleting') : t('common.actions.delete')}
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDialogClose}
        loading={operationLoading}
      />
    </Box>
  );
};

export default CasesSidebar;
